import { Head, router } from '@inertiajs/react'
import { withWorkspaceLayout } from '@/app/layouts/WorkspaceLayout'
import { type PageNode } from '@/widgets/sidebar/PageTree'
import { useState, useMemo, useCallback, useRef } from 'react'
import { diffLines, type Change } from 'diff'
import cable from '@/shared/lib/cable'

interface ChangeRequestDetail {
  id: number
  action: 'edit' | 'create_page' | 'create_folder'
  page: { id: number; title: string; slug: string; path: string } | null
  proposed_title: string | null
  proposed_parent: { id: number; title: string; path: string } | null
  content_md: string | null
  author: { name: string }
  status: string
  source: string
  batch_id: string | null
  created_at: string
  reviewer_comment: string | null
  parent_change_request_id: number | null
}

interface BatchItem extends ChangeRequestDetail {
  current_content: string | null
}

interface ReviewChangeRequestProps {
  workspace: { slug: string; name: string }
  sidebar_pages: PageNode[]
  change_request: ChangeRequestDetail
  current_content: string | null
  batch: BatchItem[] | null
  source_text: string | null
}

function DiffView({ oldText, newText }: { oldText: string; newText: string }) {
  const changes = useMemo(() => diffLines(oldText, newText), [oldText, newText])

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-50">
      <pre className="text-sm leading-6">
        {changes.map((change: Change, i: number) => {
          const lines = change.value.replace(/\n$/, '').split('\n')
          return lines.map((line: string, j: number) => {
            let className = 'px-4 block'
            let prefix = ' '
            if (change.added) {
              className += ' bg-green-50 text-green-800'
              prefix = '+'
            } else if (change.removed) {
              className += ' bg-red-50 text-red-800'
              prefix = '-'
            } else {
              className += ' text-gray-700'
            }
            return (
              <span key={`${i}-${j}`} className={className}>
                <span className="mr-2 inline-block w-4 select-none text-gray-400">{prefix}</span>
                {line}
              </span>
            )
          })
        })}
      </pre>
    </div>
  )
}

function ContentPreview({ content }: { content: string }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-green-200 bg-green-50">
      <pre className="p-4 text-sm leading-6 text-green-800 whitespace-pre-wrap">{content}</pre>
    </div>
  )
}

function ActionBadge({ action }: { action: string }) {
  const colors = action === 'edit'
    ? 'bg-blue-50 text-blue-700 ring-blue-600/20'
    : 'bg-green-50 text-green-700 ring-green-600/20'
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${colors}`}>
      {action === 'edit' ? 'Edit' : action === 'create_page' ? 'New page' : 'New folder'}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'approved') {
    return (
      <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
        Approved
      </span>
    )
  }
  if (status === 'rejected') {
    return (
      <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
        Rejected
      </span>
    )
  }
  return null
}

function flattenPages(nodes: PageNode[], depth = 0): { id: number; label: string; is_folder: boolean }[] {
  const result: { id: number; label: string; is_folder: boolean }[] = []
  for (const node of nodes) {
    result.push({ id: node.id, label: '\u00A0\u00A0'.repeat(depth) + node.title, is_folder: !!node.is_folder })
    if (node.children?.length) {
      result.push(...flattenPages(node.children, depth + 1))
    }
  }
  return result
}

function CrCard({ cr, currentContent, pages, isReassigning, onReassign, isOrphaned, onApprove, onApproveAndOpen, onReject, processingAction }: {
  cr: ChangeRequestDetail
  currentContent: string | null
  pages?: PageNode[]
  isReassigning?: boolean
  onReassign?: (targetId: number) => void
  isOrphaned?: boolean
  onApprove?: () => void
  onApproveAndOpen?: () => void
  onReject?: () => void
  processingAction?: string | null
}) {
  const [showPicker, setShowPicker] = useState(false)
  const title = cr.action === 'edit' ? cr.page?.title : cr.proposed_title
  const parentPath = cr.action !== 'edit' && cr.proposed_parent ? cr.proposed_parent.path : cr.page?.path
  const isResolved = cr.status !== 'pending'
  const isProcessing = !!processingAction

  return (
    <div className={`rounded-lg border p-5 ${isResolved ? 'border-gray-100 bg-gray-50 opacity-60' : 'border-gray-200 bg-white'}${isReassigning ? ' relative' : ''}`}>
      <div className="mb-3 flex items-center gap-2">
        <ActionBadge action={cr.action} />
        {isResolved && <StatusBadge status={cr.status} />}
        <h3 className={`text-lg font-semibold ${isResolved ? 'text-gray-500' : 'text-gray-900'}`}>{title}</h3>
        {parentPath && (
          <span className="text-sm text-gray-400">in {parentPath}</span>
        )}
        {!isResolved && pages && onReassign && !isReassigning && (
          <button
            onClick={() => setShowPicker(!showPicker)}
            className="ml-auto text-xs text-indigo-600 hover:text-indigo-800"
          >
            Reassign
          </button>
        )}
      </div>

      {isOrphaned && !isResolved && (
        <div className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 ring-1 ring-inset ring-amber-600/20">
          Parent folder was rejected — reassign to another folder or reject
        </div>
      )}

      {showPicker && pages && onReassign && (
        <div className="mb-3 flex items-center gap-2">
          <select
            className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            defaultValue=""
            onChange={(e) => {
              onReassign(Number(e.target.value))
              setShowPicker(false)
            }}
          >
            <option value="" disabled>Select new target...</option>
            {flattenPages(pages).map(p => (
              <option key={p.id} value={p.id}>
                {p.label}{p.is_folder ? ' \u{1F4C1}' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {cr.action === 'edit' ? (
        <DiffView oldText={currentContent || ''} newText={cr.content_md || ''} />
      ) : cr.content_md ? (
        <ContentPreview content={cr.content_md} />
      ) : (
        <p className="text-sm italic text-gray-400">Empty folder</p>
      )}

      {!isResolved && onApprove && (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={onApprove}
            disabled={isProcessing}
            className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {processingAction === 'approve' ? 'Approving...' : 'Approve'}
          </button>
          <button
            type="button"
            onClick={onApproveAndOpen}
            disabled={isProcessing}
            className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {processingAction === 'approve_open' ? 'Approving...' : 'Approve & Open'}
          </button>
          <button
            type="button"
            onClick={onReject}
            disabled={isProcessing}
            className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {processingAction === 'reject' ? 'Rejecting...' : 'Reject'}
          </button>
        </div>
      )}

      {isReassigning && (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/80">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Regenerating...
          </div>
        </div>
      )}
    </div>
  )
}

function ReviewChangeRequest({
  workspace,
  sidebar_pages,
  change_request,
  current_content,
  batch,
  source_text,
}: ReviewChangeRequestProps) {
  const [comment, setComment] = useState('')
  const [reassigning, setReassigning] = useState<Record<number, boolean>>({})
  const [updatedItems, setUpdatedItems] = useState<Record<number, Partial<BatchItem>>>({})
  const [processing, setProcessing] = useState<Record<number, string>>({})
  const [batchProcessing, setBatchProcessing] = useState<string | null>(null)
  const subscriptionsRef = useRef<Record<number, ReturnType<typeof cable.subscriptions.create>>>({})

  const isBatch = batch && batch.length > 1
  const items = (isBatch ? batch : [{ ...change_request, current_content }]).map(item => ({
    ...item,
    ...(updatedItems[item.id] || {})
  }))

  const pendingCount = items.filter(i => i.status === 'pending').length

  const rejectedIds = new Set(items.filter(i => i.status === 'rejected').map(i => i.id))
  const isOrphaned = (item: ChangeRequestDetail) => {
    if (!item.parent_change_request_id) return false
    return rejectedIds.has(item.parent_change_request_id)
  }

  const handleReassign = useCallback((crId: number, targetId: number) => {
    setReassigning(prev => ({ ...prev, [crId]: true }))

    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
    fetch(`/api/${workspace.slug}/change_requests/${crId}/reassign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
      body: JSON.stringify({ target_id: targetId }),
    })

    const sub = cable.subscriptions.create(
      { channel: 'ChangeRequestChannel', change_request_id: crId },
      {
        received(data: { type: string; change_request?: Partial<BatchItem>; error?: string }) {
          if (data.type === 'reassign_complete' && data.change_request) {
            setReassigning(prev => ({ ...prev, [crId]: false }))
            setUpdatedItems(prev => ({ ...prev, [crId]: data.change_request! }))
            sub.unsubscribe()
            delete subscriptionsRef.current[crId]
          } else if (data.type === 'reassign_error') {
            setReassigning(prev => ({ ...prev, [crId]: false }))
            alert(`Reassign failed: ${data.error}`)
            sub.unsubscribe()
            delete subscriptionsRef.current[crId]
          }
        }
      }
    )
    subscriptionsRef.current[crId] = sub
  }, [workspace.slug])

  const handleItemAction = (crId: number, action: 'approve' | 'approve_open' | 'reject') => {
    const processingKey = action
    setProcessing(prev => ({ ...prev, [crId]: processingKey }))

    const params = new URLSearchParams()
    if (comment) params.set('comment', comment)
    if (action === 'approve_open') params.set('redirect_to_page', 'true')

    const endpoint = action === 'reject' ? 'reject' : 'approve'
    router.post(
      `/${workspace.slug}/change_requests/${crId}/${endpoint}?${params}`,
      {},
      { onFinish: () => setProcessing(prev => { const next = { ...prev }; delete next[crId]; return next }) }
    )
  }

  const handleBatchAction = (action: 'approve' | 'reject') => {
    setBatchProcessing(action)
    const params = new URLSearchParams({ all: 'true' })
    if (comment) params.set('comment', comment)

    router.post(
      `/${workspace.slug}/change_requests/${change_request.id}/${action}?${params}`,
      {},
      { onFinish: () => setBatchProcessing(null) }
    )
  }

  const title = isBatch
    ? `Review Batch — ${batch.length} changes`
    : change_request.action === 'edit'
      ? `Review: ${change_request.page?.title}`
      : `Review: ${change_request.proposed_title}`

  return (
    <>
      <Head title={title} />
      <div className="mx-auto max-w-5xl px-6 py-8">
        <h2 className="text-2xl font-bold text-gray-900">
          {isBatch ? 'Review Batch' : 'Review Change Request'}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Author: <span className="font-medium">{change_request.author.name}</span>
          {' | '}
          Submitted: {new Date(change_request.created_at).toLocaleDateString()}
          {isBatch && (
            <> | <span className="font-medium">{pendingCount} of {batch.length} pending</span></>
          )}
        </p>

        {source_text && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h3 className="mb-2 text-sm font-medium text-gray-500">Source text</h3>
            <pre className="whitespace-pre-wrap text-sm text-gray-700">{source_text}</pre>
          </div>
        )}

        <div className="mt-6 space-y-4">
          {items.map((item) => (
            <CrCard
              key={item.id}
              cr={item}
              currentContent={'current_content' in item ? item.current_content : null}
              pages={sidebar_pages}
              isReassigning={!!reassigning[item.id]}
              onReassign={(targetId) => handleReassign(item.id, targetId)}
              isOrphaned={isOrphaned(item)}
              onApprove={() => handleItemAction(item.id, 'approve')}
              onApproveAndOpen={() => handleItemAction(item.id, 'approve_open')}
              onReject={() => handleItemAction(item.id, 'reject')}
              processingAction={processing[item.id] || null}
            />
          ))}
        </div>

        <div className="mt-6">
          <label htmlFor="comment" className="block text-sm font-medium text-gray-700">
            Reviewer Comment (optional)
          </label>
          <textarea
            id="comment"
            rows={3}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a comment for the author..."
          />
        </div>

        {isBatch && pendingCount > 0 && (
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={() => handleBatchAction('approve')}
              disabled={!!batchProcessing}
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              {batchProcessing === 'approve' ? 'Approving...' : `Approve all (${pendingCount})`}
            </button>
            <button
              type="button"
              onClick={() => handleBatchAction('reject')}
              disabled={!!batchProcessing}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {batchProcessing === 'reject' ? 'Rejecting...' : `Reject all (${pendingCount})`}
            </button>
          </div>
        )}

        {pendingCount === 0 && (
          <div className="mt-6">
            <a
              href={`/${workspace.slug}/change_requests`}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
            >
              &larr; Back to change requests
            </a>
          </div>
        )}
      </div>
    </>
  )
}

ReviewChangeRequest.layout = withWorkspaceLayout
export default ReviewChangeRequest
